import classNames from 'classnames';
import { type ComponentProps, type FC, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import TextInputField from '@/components/Form/Fields/TextInputField';

type Props = {
  inputProps: Omit<ComponentProps<typeof TextInputField>, 'onChange'> & {
    onChange: (val: string) => void;
  };
  onSelectSuggestion?: () => void;
  suggestions?: string[];
  isLoading?: boolean;
};

const TextInputWithSuggestions: FC<Props> = ({
  inputProps,
  onSelectSuggestion,
  suggestions,
  isLoading,
}) => {
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showSuggestion, setShowSuggestion] = useState(true);
  const { ref, inView, entry } = useInView({ threshold: 1, triggerOnce: true });

  const suggestionEnabled =
    (inputProps.value?.toString()?.length ?? 0) > 0 &&
    (suggestions?.length ?? 0) > 0 &&
    showSuggestion;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    inputProps.onKeyDown?.(e);

    if (!showSuggestion || !suggestionEnabled) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowSuggestion(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestionIndex < (suggestions?.length ?? 0) - 1)
        setSuggestionIndex((prev) => prev + 1);
    }
    if (suggestionIndex !== 0 && e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionIndex((prev) => prev - 1);
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      inputProps.onChange?.(suggestions?.[suggestionIndex] ?? '');
      setShowSuggestion(false);
    }
    if (e.key === 'Escape') {
      setShowSuggestion(false);
    }
  };

  useEffect(() => {
    if (suggestionEnabled) setSuggestionIndex(0);
  }, [suggestionEnabled, JSON.stringify(suggestions)]);

  useEffect(() => {
    if (!inView) entry?.target.scrollIntoView({ block: 'nearest' });
  }, [inView, entry?.target]);

  return (
    <div className="group relative w-full flex-1">
      <TextInputField
        sizeVariant="xs"
        placeholder="Job Title"
        prefixIcon="material-symbols-light:search"
        prefixIconClassName="!text-neutral-1000"
        className=" flex-1 !border-[#7C6D6D] text-neutral-1000 focus-within:!border-[#9585FA]"
        variant="transparentNeutralBorder"
        {...inputProps}
        onChange={(e) => {
          setShowSuggestion(true);
          inputProps.onChange(e.target.value);
        }}
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={onKeyDown}
        suffixElement={
          isLoading ? (
            <div className="flex gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="h-1 w-1 animate-pulse-full bg-black"
                  style={{ animationDelay: `${i * 160}ms` }}
                />
              ))}
            </div>
          ) : undefined
        }
      />
      <div
        className={classNames(
          'absolute overflow-auto flex flex-col z-10 max-h-72 border w-full divide-y rounded-lg opacity-0 pointer-events-none bg-neutral-0 shadow-md transition-opacity',
          {
            'group-focus-within:opacity-100 group-focus-within:pointer-events-auto':
              suggestionEnabled,
          }
        )}
      >
        {suggestions?.map((suggestion, i) => (
          <button
            tabIndex={-1}
            ref={i === suggestionIndex ? ref : undefined}
            onMouseEnter={() => setSuggestionIndex(i)}
            type="button"
            className={classNames(
              'cursor-pointer px-3 font-normal py-2 my-1 text-left text-body-small text-neutral-1000',
              {
                'bg-black !text-neutral-0': i === suggestionIndex,
              }
            )}
            key={suggestion}
            onClick={() => {
              inputProps.onChange?.(suggestion);
              onSelectSuggestion?.();
            }}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
};

export default TextInputWithSuggestions;
